import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ShieldAlert, Check, HeartCrack } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AgeVerificationPopup = () => {
  const [status, setStatus] = useState("checking"); // checking | ask | refused | done
  const [checked, setChecked] = useState(false);
  const [shake, setShake] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Le cookie httpOnly est posé par le serveur : on interroge le backend (impossible à falsifier côté client)
    axios
      .get(`${API}/age-verification/status`, { withCredentials: true })
      .then((r) => setStatus(r.data.confirmed ? "done" : "ask"))
      .catch(() => setStatus("ask"));
  }, []);

  const handleConfirm = async () => {
    if (!checked) {
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/age-verification/confirm`, {}, { withCredentials: true });
      setStatus("done");
    } catch {
      // en cas d'échec réseau, on laisse tout de même entrer (le gate reste demandé au prochain chargement)
      setStatus("done");
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "checking" || status === "done") return null;

  return (
    <AnimatePresence>
      <motion.div
        key="age-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[999] flex items-center justify-center p-4"
        style={{ backgroundColor: "rgba(28,25,23,0.72)", backdropFilter: "blur(14px)" }}
        data-testid="age-verification-overlay"
      >
        {status === "ask" ? (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className={`relative w-full max-w-md rounded-3xl bg-[#1C1917] border border-white/10 shadow-2xl overflow-hidden ${shake ? "animate-[wiggle_0.4s_ease-in-out]" : ""}`}
            data-testid="age-verification-card"
          >
            {/* Bande d'accent */}
            <div className="h-1.5 w-full" style={{ background: "linear-gradient(90deg,#E05638 0%,#D4AF37 50%,#0A5C36 100%)" }} />

            <div className="px-7 sm:px-9 pt-8 pb-8">
              <div className="flex justify-center mb-5">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl" style={{ backgroundColor: "rgba(224,86,56,0.15)" }}>
                  <ShieldAlert size={32} strokeWidth={1.8} style={{ color: "#E05638" }} />
                </div>
              </div>

              <p className="text-center text-[11px] font-bold tracking-[0.2em] uppercase mb-2" style={{ color: "#D4AF37" }}>
                Accès réservé aux +18 ans
              </p>
              <h2 className="font-heading text-3xl font-extrabold text-center text-white mb-5 leading-tight">
                Vous avez 18 ans<br />ou plus&nbsp;?
              </h2>

              <div className="space-y-3 text-center mb-7">
                <p className="text-sm text-stone-300 leading-relaxed">
                  L'accès à <span className="font-semibold text-white">FodindinCA</span> est
                  strictement réservé aux personnes âgées de <span className="font-semibold text-white">18 ans ou plus</span>.
                </p>
                <p className="text-sm text-stone-400 leading-relaxed">
                  En continuant, vous confirmez que vous remplissez cette condition et acceptez nos conditions d'utilisation.
                </p>
              </div>

              {/* Case à cocher obligatoire */}
              <button
                type="button"
                onClick={() => setChecked((v) => !v)}
                className="flex w-full items-center gap-3 rounded-2xl border p-4 mb-5 text-left transition-all"
                style={{
                  borderColor: checked ? "#0A5C36" : "rgba(255,255,255,0.14)",
                  backgroundColor: checked ? "rgba(10,92,54,0.18)" : "rgba(255,255,255,0.03)",
                }}
                data-testid="age-verification-checkbox"
                aria-pressed={checked}
              >
                <span
                  className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md border-2 transition-all"
                  style={{
                    borderColor: checked ? "#0A5C36" : "rgba(255,255,255,0.3)",
                    backgroundColor: checked ? "#0A5C36" : "transparent",
                  }}
                >
                  {checked && <Check size={16} strokeWidth={3} className="text-white" />}
                </span>
                <span className="text-sm font-medium text-stone-200">
                  Je confirme avoir 18 ans ou plus.
                </span>
              </button>

              <button
                onClick={handleConfirm}
                disabled={!checked || submitting}
                className="w-full rounded-full py-3.5 font-heading font-bold text-white transition-all duration-300 disabled:cursor-not-allowed"
                style={{
                  background: checked ? "linear-gradient(90deg,#E05638,#D4AF37)" : "rgba(255,255,255,0.08)",
                  color: checked ? "#1C1917" : "rgba(255,255,255,0.35)",
                }}
                data-testid="age-verification-confirm-btn"
              >
                {submitting ? "Un instant…" : "Entrer sur le site"}
              </button>

              <button
                onClick={() => setStatus("refused")}
                className="mt-3 w-full text-center text-xs font-medium text-stone-500 hover:text-stone-300 transition-colors"
                data-testid="age-verification-refuse-btn"
              >
                J'ai moins de 18 ans — Quitter
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative w-full max-w-md rounded-3xl bg-[#1C1917] border border-white/10 shadow-2xl px-8 py-10 text-center"
            data-testid="age-verification-refused"
          >
            <div className="flex justify-center mb-5">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl" style={{ backgroundColor: "rgba(224,86,56,0.15)" }}>
                <HeartCrack size={32} strokeWidth={1.8} style={{ color: "#E05638" }} />
              </div>
            </div>
            <h2 className="font-heading text-2xl font-extrabold text-white mb-3">Accès refusé</h2>
            <p className="text-sm text-stone-400 leading-relaxed mb-6">
              Désolé, ce site est réservé aux personnes majeures (18 ans et plus).
              Revenez nous voir quand vous aurez l'âge requis&nbsp;!
            </p>
            <button
              onClick={() => setStatus("ask")}
              className="text-xs font-semibold underline text-stone-400 hover:text-white transition-colors"
              data-testid="age-verification-back-btn"
            >
              Revenir en arrière
            </button>
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default AgeVerificationPopup;
